import React from 'react';
import { CreditCard, CheckCircle, Info } from 'lucide-react';
import {
  isProductEligible,
  financingPriceFromNet,
  financingPriceFromGross,
  PAYPAL_INSTALLMENT_DISCLAIMER,
  PAYPAL_MAX_AMOUNT_EUR,
} from '../utils/paypalEligibility';

const fmtEur = (n) =>
  Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

/**
 * Renders a "Mit PayPal-Ratenzahlung kaufen" block on a product detail page
 * if the product is eligible. Shows both the financing net price (+10 %)
 * and the resulting gross price, plus the legal disclaimer.
 *
 * Props:
 *   product: { slug, category, isAccessory, name }
 *   netPrice: NET price in EUR (preferred — accurate +10 % calc)
 *   grossPrice: GROSS price in EUR (used as fallback if no netPrice)
 *   onSelect: optional callback when the CTA button is clicked
 */
const PayPalInstallmentOption = ({ product, netPrice, grossPrice, onSelect }) => {
  if (!product) return null;

  const eligible = isProductEligible({
    slug: product.slug,
    category: product.category,
    isAccessory: !!product.isAccessory,
    stockQuantity: product.stockQuantity,
    availability: product.availability,
  });
  if (!eligible) return null;

  // Compute financing price breakdown
  const breakdown =
    netPrice != null && Number(netPrice) > 0
      ? financingPriceFromNet(netPrice)
      : grossPrice != null && Number(grossPrice) > 0
      ? financingPriceFromGross(grossPrice)
      : null;
  if (!breakdown) return null;

  // Hide if the financed gross exceeds the PayPal cap for a single item
  if (breakdown.grossFinanced > PAYPAL_MAX_AMOUNT_EUR + 0.005) return null;

  // Monthly rate (24 months default)
  const monthly = breakdown.grossFinanced / 24;
  const monthlyStr = monthly.toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <div
      className="mt-4 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-4 sm:p-5"
      data-testid="paypal-installment-option"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-700 text-white flex-shrink-0">
          <CreditCard size={20} />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-gray-900 text-base">PayPal-Ratenzahlung verfügbar</h4>
          <p className="text-xs text-gray-600">0 % Zinsen für Sie – die Finanzierung übernimmt Jannesmann</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-blue-100 p-3 mb-3 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Verkaufspreis brutto:</span>
          <span className="text-gray-700">{fmtEur(breakdown.grossOriginal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">+ PayPal-Finanzierungsgebühr (10 %):</span>
          <span className="text-gray-700">{fmtEur(breakdown.surchargeGross)}</span>
        </div>
        <div className="flex justify-between text-base font-bold border-t pt-1.5 mt-1.5">
          <span className="text-gray-900">Finanzierungspreis (24 Monate):</span>
          <span className="text-blue-700">{fmtEur(breakdown.grossFinanced)}</span>
        </div>
        <div className="flex justify-between text-sm pt-1">
          <span className="text-gray-700">monatlich:</span>
          <span className="font-bold text-blue-700">{monthlyStr} €</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onSelect?.()}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg transition-colors"
        data-testid="paypal-installment-cta"
      >
        <CreditCard size={18} />
        Mit PayPal-Ratenzahlung kaufen
      </button>

      <div className="mt-3 space-y-2">
        <div className="flex items-start gap-2 text-xs text-green-700">
          <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>Verfügbar für Einkaufsbeträge bis {PAYPAL_MAX_AMOUNT_EUR.toLocaleString('de-DE')} € brutto</span>
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span><strong>Hinweis:</strong> Nur für Privatkunden mit Privat-PayPal-Konto verfügbar (keine PayPal-Geschäftskonten).</span>
        </div>
        <div className="flex items-start gap-2 text-[11px] text-gray-500 leading-relaxed">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          <span>{PAYPAL_INSTALLMENT_DISCLAIMER}</span>
        </div>
      </div>
    </div>
  );
};

export default PayPalInstallmentOption;
